import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/Shared/Button';
import { Table } from '../../components/Shared/Table';
import {
  WorkflowAssociationRuleFormModal,
  type WorkflowRuleDimension,
} from '../../components/Admin/WorkflowAssociationRuleFormModal';
import { useToast } from '../../components/UI/ToastProvider';
import { workflowAssociationService, type WorkflowAssociationRuleDto } from '../../services/workflowAssociationService';

function getCustomerIdFromStorage(): string {
  return sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ?? '';
}

type TabId = 'extension' | 'template' | 'folder';

function partitionRules(rules: WorkflowAssociationRuleDto[]) {
  const byExtension: WorkflowAssociationRuleDto[] = [];
  const byTemplate: WorkflowAssociationRuleDto[] = [];
  const byFolder: WorkflowAssociationRuleDto[] = [];
  for (const r of rules) {
    if (r.extension != null && r.extension !== '') {
      byExtension.push(r);
    } else if (r.templateId != null && r.templateId !== '') {
      byTemplate.push(r);
    } else if (r.folderId != null && r.folderId !== '') {
      byFolder.push(r);
    }
  }
  return { byExtension, byTemplate, byFolder };
}

function buildUpdateDto(rule: WorkflowAssociationRuleDto, isActive: boolean) {
  return {
    workflowId: rule.workflowId,
    isActive,
    extension: rule.extension != null && rule.extension !== '' ? rule.extension : undefined,
    templateId: rule.templateId != null && rule.templateId !== '' ? rule.templateId : undefined,
    folderId: rule.folderId != null && rule.folderId !== '' ? rule.folderId : undefined,
  };
}

function ruleDimension(rule: WorkflowAssociationRuleDto): TabId {
  if (rule.extension != null && rule.extension !== '') {
    return 'extension';
  }
  if (rule.templateId != null && rule.templateId !== '') {
    return 'template';
  }
  return 'folder';
}

function RulesTableSkeleton() {
  return (
    <div className="animate-pulse space-y-3" aria-hidden>
      <div className="h-8 bg-gray-200 rounded w-2/3" />
      <div className="h-10 bg-gray-100 rounded" />
      <div className="h-10 bg-gray-100 rounded" />
      <div className="h-10 bg-gray-100 rounded" />
      <div className="h-10 bg-gray-100 rounded" />
    </div>
  );
}

export const WorkflowAssociationRulesPage: React.FC = () => {
  const customerId = useMemo(() => getCustomerIdFromStorage(), []);
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState<TabId>('extension');
  const [rules, setRules] = useState<WorkflowAssociationRuleDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalDimension, setModalDimension] = useState<WorkflowRuleDimension>('extension');
  const [editingRule, setEditingRule] = useState<WorkflowAssociationRuleDto | null>(null);

  const { byExtension, byTemplate, byFolder } = useMemo(() => partitionRules(rules), [rules]);

  const loadData = useCallback(async () => {
    if (!customerId) {
      setLoadError('Cliente não identificado na sessão. Faça login novamente.');
      setRules([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await workflowAssociationService.getRules(customerId);
      setRules(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erro ao carregar regras.';
      setLoadError(message);
      addToast(message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [customerId, addToast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const openCreate = (dim: TabId) => {
    setModalDimension(dim);
    setEditingRule(null);
    setModalOpen(true);
  };

  const openEdit = (rule: WorkflowAssociationRuleDto) => {
    setModalDimension(ruleDimension(rule));
    setEditingRule(rule);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (modalOpen) {
      setModalOpen(false);
      setEditingRule(null);
    }
  };

  const handleToggle = async (rule: WorkflowAssociationRuleDto, next: boolean) => {
    setTogglingId(rule.id);
    try {
      await workflowAssociationService.updateRule(rule.id, { ...buildUpdateDto(rule, next) });
      addToast('Regra atualizada.', 'success');
      await loadData();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Não foi possível atualizar a regra.';
      addToast(message, 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = (rule: WorkflowAssociationRuleDto) => {
    if (!window.confirm('Excluir esta regra de associação?')) {
      return;
    }
    if (rule.isActive) {
      const second =
        'Esta regra está ativa e pode ter sido usada para atribuir workflow a documentos. A exclusão não altera documentos já existentes — apenas deixa de sugerir este vínculo para documentos novos. Confirma a exclusão definitiva?';
      if (!window.confirm(second)) {
        return;
      }
    }
    void performDelete(rule.id);
  };

  const performDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await workflowAssociationService.deleteRule(id);
      addToast('Regra excluída.', 'success');
      await loadData();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Não foi possível excluir a regra.';
      addToast(message, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const priorityTooltip =
    'Cada regra associa um workflow a uma única dimensão: extensão, template OU pasta. Para combinar critérios, crie regras separadas — a prioridade de resolução é Manual > Template > Extensão > Pasta (com herança) > Padrão da empresa.';

  const renderEmpty = (msg: string) => (
    <div className="text-center py-10 px-4 rounded-lg border border-dashed border-gray-200 bg-gray-50/80">
      <p className="text-sm text-gray-600">{msg}</p>
    </div>
  );

  const makeColumns = (kind: 'extension' | 'template' | 'folder') => {
    return [
      kind === 'extension'
        ? {
            header: 'Extensão',
            accessor: 'extension',
            cell: (r: WorkflowAssociationRuleDto) => <span className="font-mono text-gray-900">{r.extension}</span>,
          }
        : kind === 'template'
          ? {
              header: 'Template',
              accessor: 'templateName',
              cell: (r: WorkflowAssociationRuleDto) => (
                <span className="text-gray-900">{r.templateName ?? r.templateId}</span>
              ),
            }
          : {
              header: 'Pasta',
              accessor: 'folderPath',
              cell: (r: WorkflowAssociationRuleDto) => (
                <span className="font-mono text-sm text-gray-900 break-all max-w-md inline-block">
                  {r.folderPath ?? r.folderId}
                </span>
              ),
            },
      {
        header: 'Workflow',
        accessor: 'workflowName',
        cell: (r: WorkflowAssociationRuleDto) => <span className="text-gray-900">{r.workflowName ?? r.workflowId}</span>,
      },
      {
        header: 'Ativa',
        accessor: 'isActive',
        cell: (r: WorkflowAssociationRuleDto) => (
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-600"
            checked={r.isActive}
            disabled={togglingId === r.id}
            onChange={(e) => void handleToggle(r, e.target.checked)}
            title={r.isActive ? 'Desativar regra' : 'Ativar regra'}
          />
        ),
      },
      {
        header: 'Ações',
        accessor: 'actions',
        cell: (r: WorkflowAssociationRuleDto) => (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => openEdit(r)} title="Editar">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => confirmDelete(r)}
              disabled={deletingId === r.id}
              title="Excluir"
            >
              {deletingId === r.id ? (
                '…'
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </Button>
          </div>
        ),
      },
    ];
  };

  const tabDefs: { id: TabId; label: string; data: WorkflowAssociationRuleDto[]; empty: string }[] = [
    {
      id: 'extension',
      label: 'Por extensão',
      data: byExtension,
      empty: 'Nenhuma regra cadastrada por extensão. Crie uma para começar.',
    },
    {
      id: 'template',
      label: 'Por template',
      data: byTemplate,
      empty: 'Nenhuma regra cadastrada por template. Crie uma para começar.',
    },
    {
      id: 'folder',
      label: 'Por pasta',
      data: byFolder,
      empty: 'Nenhuma regra cadastrada por pasta. Crie uma para começar.',
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="sm:flex sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Associação de Workflow</h1>
            <span
              className="inline-flex"
              title={priorityTooltip}
            >
              <InformationCircleIcon className="h-5 w-5 text-blue-500 cursor-help" aria-hidden />
              <span className="sr-only">Informação sobre invariável e prioridade de resolução</span>
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-700 max-w-3xl">
            Cada regra define qual workflow será aplicado automaticamente quando um documento é criado, conforme a extensão
            do arquivo, o template ou a pasta de destino.
          </p>
        </div>
      </div>

      {loadError && (
        <div className="mt-4 rounded-md bg-red-50 text-red-800 px-4 py-3 text-sm flex justify-between gap-3">
          <span>{loadError}</span>
          <button type="button" className="underline font-medium" onClick={() => setLoadError(null)}>
            Fechar
          </button>
        </div>
      )}

      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-2 sm:space-x-6 sm:gap-0" aria-label="Abas de dimensão">
          {tabDefs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {tabDefs.map((tab) => {
        if (activeTab !== tab.id) {
          return null;
        }
        return (
          <div key={tab.id} className="mt-6">
            <div className="flex justify-end mb-3">
              <Button variant="primary" onClick={() => openCreate(tab.id)}>
                Nova regra
              </Button>
            </div>
            {isLoading ? (
              <RulesTableSkeleton />
            ) : tab.data.length > 0 ? (
              <Table data={tab.data} columns={makeColumns(tab.id)} />
            ) : (
              renderEmpty(tab.empty)
            )}
          </div>
        );
      })}

      <WorkflowAssociationRuleFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        customerId={customerId}
        dimension={modalDimension}
        initialData={editingRule ?? undefined}
        onSaved={() => void loadData()}
      />
    </div>
  );
};
